import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  Alert,
  Paper,
  Card,
  CardContent,
  Grid,
  alpha,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import { useUsers } from "../../hooks/useRedux";
import { User } from "../../store/slices/types";
import { toast } from "react-toastify";

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const { users, loading, error, updateUser, refetch, pagination } = useUsers(paginationModel.page + 1, paginationModel.pageSize);

  if (error) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const formatDate = (date: number | string | Date | undefined) => {
    if (!date) return "N/A";
    const dateObj = typeof date === 'number' ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) return "N/A";
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleStatusToggle = async (user: User, newStatus: boolean) => {
    try {
      const userId = user._id || user.id || user.uid;
      if (!userId) {
        toast.error("Invalid user ID");
        return;
      }
      await updateUser(userId, {
        preferences: {
          ...user.preferences,
          isActive: newStatus,
        },
      });
      // Refresh the data after update
      await refetch();
      toast.success(newStatus ? "user is activated" : "user is deactivated");
    } catch (error) {
      console.error("Failed to update user status:", error);
      toast.error("❌ Failed to update user status");
    }
  };

  const columns: GridColDef[] = [
    {
      field: "avatar",
      headerName: "",
      flex: 0.1,
      minWidth: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const profilePhoto = params.row.profilePhoto;
        const displayName = params.row.displayName || params.row.fullName || params.row.username || params.row.email || "Unknown";
        const initials = displayName.charAt(0).toUpperCase();
        
        return (
          <Avatar
            src={profilePhoto}
            alt={displayName}
            sx={{
              width: 40,
              height: 40,
              bgcolor: profilePhoto ? "transparent" : "primary.main",
              fontWeight: "bold",
            }}
          >
            {!profilePhoto && initials}
          </Avatar>
        );
      },
    },
    {
      field: "displayName",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
      filterable: true,
      valueGetter: (params) =>
        params.row.displayName ||
        params.row.fullName ||
        params.row.username ||
        params.row.email ||
        "Unknown",
      renderCell: (params) => {
        const userId = params.row._id || params.row.id || params.row.uid || "";
        const displayName =
          params.row.displayName ||
          params.row.fullName ||
          params.row.username ||
          params.row.email ||
          "Unknown";
        return (
          <Box>
            <Typography variant='body2' fontWeight='600'>
              {displayName}
            </Typography>
            {userId && (
              <Typography variant='caption' color='textSecondary'>
                ID: {userId.substring(0, 8)}...
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.2,
      minWidth: 200,
      filterable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <EmailIcon sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
          <Typography variant='body2' noWrap>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2'>{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.6,
      minWidth: 120,
      renderCell: (params) => {
        const user = params.row;
        // Simple logic: admin toggle directly controls status
        const isActive = user.preferences?.isActive !== false; // Default to true if not set

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={isActive ? "Active" : "Inactive"}
              color={isActive ? "success" : "default"}
              size='small'
              variant='filled'
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => handleStatusToggle(user, e.target.checked)}
                  size='small'
                />
              }
              label=''
              sx={{ margin: 0 }}
            />
          </Box>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 0.3,
      minWidth: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title='Edit User'>
              <EditIcon />
            </Tooltip>
          }
          label='Edit'
          onClick={() => handleEdit(params.row)}
        />,
      ],
    },
  ];

  const handleAdd = () => {
    navigate("/users/add");
  };

  const handleEdit = (user: User) => {
    const id = user._id || user.id || user.uid;
    if (id) {
      navigate(`/users/edit/${id}`);
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant='h4' fontWeight='700' gutterBottom>
              User Management
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              Manage and monitor your wellness app users
            </Typography>
          </Box>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
              },
            }}
          >
            Add User
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant='h4' fontWeight='bold'>
                      {users.length}
                    </Typography>
                    <Typography variant='body2' sx={{ opacity: 0.9 }}>
                      Total Users
                    </Typography>
                  </Box>
                  <PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='success.main'
                    >
                      {
                        users.filter((u) => u.preferences?.isActive !== false)
                          .length
                      }
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Active Users
                    </Typography>
                  </Box>
                  <CheckCircleIcon
                    sx={{ fontSize: 40, color: "success.main" }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='warning.main'
                    >
                      {
                        users.filter((u) => u.preferences?.isActive === false)
                          .length
                      }
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Inactive Users
                    </Typography>
                  </Box>
                  <BlockIcon sx={{ fontSize: 40, color: "warning.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='primary.main'
                    >
                      {(() => {
                        const usersWithCreatedAt = users.filter(
                          (u) => u.createdAt
                        );
                        if (usersWithCreatedAt.length === 0) return 0;
                        const totalDays =
                          usersWithCreatedAt.reduce(
                            (acc, u) => acc + (Date.now() - (u.createdAt || 0)),
                            0
                          ) /
                          usersWithCreatedAt.length /
                          (24 * 60 * 60 * 1000);
                        return Math.round(totalDays);
                      })()}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Avg. Days Since Join
                    </Typography>
                  </Box>
                  <Typography variant='h3' color='primary.main'>
                    🔥
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Data Table */}
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <DataGrid
          rows={users}
          columns={columns}
          getRowId={(row) =>
            row._id || row.id || row.uid || String(Math.random())
          }
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={pagination?.total || 0}
          loading={loading}
          autoHeight
          disableColumnMenu={false}
          disableColumnFilter={false}
          disableColumnSelector={false}
          filterModel={undefined}
          onFilterModelChange={() => {}}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: alpha("#667eea", 0.1),
              borderBottom: `2px solid ${alpha("#667eea", 0.2)}`,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha("#667eea", 0.05),
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },
            "& .MuiDataGrid-filterForm": {
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default Users;
